import React, { Component } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import API from "../utils/api";

// Groups columns that represent the same underlying lead/call outcome so they
// can share a background color (see LEAD_STATUS_MAPPING in teleConstants.ts:
// call_status 5/8/9/11/13/15 all set lead status to Deleted, 14 sets it to
// Tele Posted).
const COLUMN_GROUPS = {
  tele_posted: "posted",
  done_count: "posted",

  deleted_count: "deleted",
  no_requirement: "deleted",
  no_dl: "deleted",
  duplicate_count: "deleted",
  spam_call_status: "deleted",
  already_done: "deleted",
  personal_use: "deleted",

  pending_count: "pending",
  phone_not_picked: "pending",
  phone_switched_off: "pending",
  phone_not_reachable: "pending",
  callback_later: "pending",
  call_pending: "pending",
};

const GROUP_COLORS = {
  posted: "#e6f4ea",
  deleted: "#fdecea",
  pending: "#fff8e1",
};

const colStyle = (field) => {
  const group = COLUMN_GROUPS[field];
  return group ? { backgroundColor: GROUP_COLORS[group] } : undefined;
};

class AdminSourceWiseDetailedReport extends Component {
  state = {
    from: "",
    to: "",
    service_type: "",
    service_for: [],
    medicine_type: [],
    location: [],
    rows: [],
    totals: {},
    loading: true,
    medicineTypeOptions: [],
    serviceForOptions: [],
    serviceTypeOptions: [],
  };

  componentDidMount() {
    this.loadFilters();
    this.fetchReport();
  }

  loadFilters = async () => {
    try {
      const constRes = await API.get("/const-data");
      const constData = constRes.data.data || constRes.data || {};
      this.setState({
        medicineTypeOptions: constData.medicine_type || [],
        serviceForOptions: constData.service_for || [],
        serviceTypeOptions: constData.enquiry_type || [],
      });
    } catch (e) {
      console.error("Failed to load filter options", e);
    }
  };

  loadLocations = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];

    try {
      const res = await API.get("/cities", { params: { q: inputValue } });
      const cities = res.data.data || res.data || [];

      return cities.map((city) => ({
        value: `${city.city}, ${city.state}`,
        label: `${city.city}, ${city.state}`,
        id: city.id,
      }));
    } catch (e) {
      return [];
    }
  };

  fetchReport = async () => {
    this.setState({ loading: true });
    try {
      const { from, to, service_type, service_for, medicine_type, location } = this.state;
      const params = {};

      if (from) params.from = from;
      if (to) params.to = to;
      if (service_type) params.service_type = service_type;
      if (service_for && service_for.length > 0) {
        params.service_for = service_for.map((item) => item.value).join(",");
      }
      if (medicine_type && medicine_type.length > 0) {
        params.medicine_type = medicine_type.map((item) => item.value).join(",");
      }
      if (location && location.length > 0) {
        params.location = JSON.stringify(location.map((item) => item.value));
      }

      const res = await API.get("/admin/report/source-wise-detailed", { params });
      const data = res.data.data || res.data || {};

      this.setState({
        rows: data.rows || [],
        totals: data.totals || {},
        loading: false,
      });
    } catch (e) {
      toast.error("Failed to load source wise detailed report");
      this.setState({ loading: false });
    }
  };

  handleSearch = (e) => {
    e.preventDefault();
    this.fetchReport();
  };

  handleReset = () => {
    this.setState(
      {
        from: "",
        to: "",
        service_type: "",
        service_for: [],
        medicine_type: [],
        location: [],
      },
      this.fetchReport
    );
  };

  getNum = (obj, key) => Number((obj && obj[key]) || 0);

  render() {
    const {
      from,
      to,
      service_type,
      service_for,
      medicine_type,
      location,
      rows,
      totals,
      loading,
      medicineTypeOptions,
      serviceForOptions,
      serviceTypeOptions,
    } = this.state;

    return (
      <div>
        <h2 className="page-header">
          <i className="fas fa-table"></i> Source Wise Detailed Report
        </h2>

        <form onSubmit={this.handleSearch} className="well" style={{ marginBottom: 20, padding: 15 }}>
          <div className="row">
            <div className="col-md-2">
              <div className="form-group">
                <label>From</label>
                <input
                  type="date"
                  className="form-control"
                  value={from}
                  onChange={(e) => this.setState({ from: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label>To</label>
                <input
                  type="date"
                  className="form-control"
                  value={to}
                  onChange={(e) => this.setState({ to: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Service Type</label>
                <select
                  className="form-control"
                  value={service_type}
                  onChange={(e) => this.setState({ service_type: e.target.value })}
                >
                  <option value="">All Service Types</option>
                  {serviceTypeOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>&nbsp;</label>
                <div>
                  <button type="submit" className="btn btn-primary" style={{ marginRight: 8 }}>
                    <i className="fas fa-search"></i> Search
                  </button>
                  <button type="button" className="btn btn-default" onClick={this.handleReset}>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>Service For</label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  options={serviceForOptions.map((s) => ({ label: s, value: s }))}
                  value={service_for}
                  onChange={(selected) => this.setState({ service_for: selected || [] })}
                  placeholder="All service for values"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Medicine Type</label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  options={medicineTypeOptions.map((s) => ({ label: s, value: s }))}
                  value={medicine_type}
                  onChange={(selected) => this.setState({ medicine_type: selected || [] })}
                  placeholder="All medicine types"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Location</label>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions={false}
                  loadOptions={this.loadLocations}
                  value={location}
                  onChange={(selected) => this.setState({ location: selected || [] })}
                  placeholder="Search city/state"
                  noOptionsMessage={() => "Type at least 2 characters"}
                />
              </div>
            </div>
          </div>
        </form>

        <div className="panel panel-default">
          <div className="panel-heading">
            <strong>Source Wise Detailed Summary</strong>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-bordered" style={{ marginBottom: 0, minWidth: 1900 }}>
              <thead>
                <tr>
                  <th rowSpan="2" className="text-center" style={{ verticalAlign: "middle" }}>Source</th>
                  <th colSpan="6" className="text-center">Summary</th>
                  <th colSpan="13" className="text-center">Call Status Report</th>
                </tr>
                <tr>
                  <th className="text-right">Total</th>
                  <th className="text-right">Direct Posted</th>
                  <th className="text-right" style={colStyle("tele_posted")}>Tele Posted</th>
                  <th className="text-right" style={colStyle("deleted_count")}>Deleted</th>
                  <th className="text-right">Spam</th>
                  <th className="text-right" style={colStyle("pending_count")}>Pending</th>

                  <th className="text-right" style={colStyle("done_count")}>Done</th>

                  <th className="text-right" style={colStyle("no_requirement")}>No Requirement</th>
                  <th className="text-right" style={colStyle("no_dl")}>No DL</th>
                  <th className="text-right" style={colStyle("duplicate_count")}>Duplicate</th>
                  <th className="text-right" style={colStyle("spam_call_status")}>Spam Call</th>
                  <th className="text-right" style={colStyle("already_done")}>Already Done</th>
                  <th className="text-right" style={colStyle("personal_use")}>Personal Use</th>

                  <th className="text-right" style={colStyle("phone_not_picked")}>Phone Not Picked</th>
                  <th className="text-right" style={colStyle("phone_switched_off")}>Switched Off</th>
                  <th className="text-right" style={colStyle("phone_not_reachable")}>Not Reachable</th>
                  <th className="text-right" style={colStyle("callback_later")}>Callback Later</th>
                  <th className="text-right" style={colStyle("call_pending")}>Call Pending</th>
                  <th className="text-right">Not Allocated</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="20" className="text-center">
                      <i className="fas fa-spinner fa-spin"></i> Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan="20" className="text-center text-muted">
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.source}>
                      <td>{row.source}</td>
                      <td className="text-right">{this.getNum(row, "total_leads")}</td>
                      <td className="text-right">{this.getNum(row, "direct_posted")}</td>
                      <td className="text-right" style={colStyle("tele_posted")}>{this.getNum(row, "tele_posted")}</td>
                      <td className="text-right" style={colStyle("deleted_count")}>{this.getNum(row, "deleted_count")}</td>
                      <td className="text-right">{this.getNum(row, "spam_count")}</td>
                      <td className="text-right" style={colStyle("pending_count")}>{this.getNum(row, "pending_count")}</td>


                      <td className="text-right" style={colStyle("done_count")}>{this.getNum(row, "done_count")}</td>

                      <td className="text-right" style={colStyle("no_requirement")}>{this.getNum(row, "no_requirement")}</td>
                      <td className="text-right" style={colStyle("no_dl")}>{this.getNum(row, "no_dl")}</td>
                      <td className="text-right" style={colStyle("duplicate_count")}>{this.getNum(row, "duplicate_count")}</td>
                      <td className="text-right" style={colStyle("spam_call_status")}>{this.getNum(row, "spam_call_status")}</td>
                      <td className="text-right" style={colStyle("already_done")}>{this.getNum(row, "already_done")}</td>
                      <td className="text-right" style={colStyle("personal_use")}>{this.getNum(row, "personal_use")}</td>

                      <td className="text-right" style={colStyle("phone_not_picked")}>{this.getNum(row, "phone_not_picked")}</td>
                      <td className="text-right" style={colStyle("phone_switched_off")}>{this.getNum(row, "phone_switched_off")}</td>
                      <td className="text-right" style={colStyle("phone_not_reachable")}>{this.getNum(row, "phone_not_reachable")}</td>
                      <td className="text-right" style={colStyle("callback_later")}>{this.getNum(row, "callback_later")}</td>
                      <td className="text-right" style={colStyle("call_pending")}>{this.getNum(row, "call_pending")}</td>
                      <td className="text-right">{this.getNum(row, "not_allocated")}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && rows.length > 0 && (
                <tfoot>
                  <tr>
                    <th>Total</th>
                    <th className="text-right">{this.getNum(totals, "total_leads")}</th>
                    <th className="text-right">{this.getNum(totals, "direct_posted")}</th>
                    <th className="text-right" style={colStyle("tele_posted")}>{this.getNum(totals, "tele_posted")}</th>
                    <th className="text-right" style={colStyle("deleted_count")}>{this.getNum(totals, "deleted_count")}</th>
                    <th className="text-right">{this.getNum(totals, "spam_count")}</th>
                    <th className="text-right" style={colStyle("pending_count")}>{this.getNum(totals, "pending_count")}</th>

                    <th className="text-right" style={colStyle("done_count")}>{this.getNum(totals, "done_count")}</th>

                    <th className="text-right" style={colStyle("no_requirement")}>{this.getNum(totals, "no_requirement")}</th>
                    <th className="text-right" style={colStyle("no_dl")}>{this.getNum(totals, "no_dl")}</th>
                    <th className="text-right" style={colStyle("duplicate_count")}>{this.getNum(totals, "duplicate_count")}</th>
                    <th className="text-right" style={colStyle("spam_call_status")}>{this.getNum(totals, "spam_call_status")}</th>
                    <th className="text-right" style={colStyle("already_done")}>{this.getNum(totals, "already_done")}</th>
                    <th className="text-right" style={colStyle("personal_use")}>{this.getNum(totals, "personal_use")}</th>


                    <th className="text-right" style={colStyle("phone_not_picked")}>{this.getNum(totals, "phone_not_picked")}</th>
                    <th className="text-right" style={colStyle("phone_switched_off")}>{this.getNum(totals, "phone_switched_off")}</th>
                    <th className="text-right" style={colStyle("phone_not_reachable")}>{this.getNum(totals, "phone_not_reachable")}</th>
                    <th className="text-right" style={colStyle("callback_later")}>{this.getNum(totals, "callback_later")}</th>

                    <th className="text-right" style={colStyle("call_pending")}>{this.getNum(totals, "call_pending")}</th>
                    <th className="text-right">{this.getNum(totals, "not_allocated")}</th>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    );
  }
}

export default AdminSourceWiseDetailedReport;
